/**
 * SciChart-based Volume Chart for Decline Curve Analysis.
 *
 * It replaced the Highcharts-based VolumeChart component, since deleted.
 * It replicates all functionality: production/forecast/type-curve series,
 * draggable forecast points, draggable plot lines, segment markers, outlier
 * markers, x-axis type switching, logarithmic axes, dual y-axes for ratio
 * charts, custom tooltips, rubber-band zoom, and responsive resizing.
 *
 * Prerequisites:
 *   yarn add scichart
 *
 * SciChart WASM must be configured once at app startup:
 *   import { SciChartSurface } from 'scichart';
 *   SciChartSurface.useWasmFromCDN();
 *   // or SciChartSurface.configure({ wasmUrl: '/path/to/scichart2d.wasm', dataUrl: '/path/to/scichart2d.data' });
 */

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import { debounce } from 'lodash-es';
import {
  SciChartSurface,
  SciChartJSLightTheme,
  NumericAxis,
  LogarithmicAxis,
  FastLineRenderableSeries,
  XyDataSeries,
  XyScatterRenderableSeries,
  EllipsePointMarker,
  MouseWheelZoomModifier,
  ZoomPanModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  CursorModifier,
  VerticalLineAnnotation,
  CustomAnnotation,
  EAxisAlignment,
  EAutoRange,
  EAnnotationLayer,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  ChartModifierBase2D,
  ModifierMouseArgs,
  IPointMetadata,
  TSciChart,
  IRenderableSeries,
  NumericLabelProvider,
  NumberRange,
  Thickness,
} from 'scichart';
import { MonthlyTickProvider } from 'components/charts';

import {
  RatioRecord,
  TypeCurveRatioRecord,
  TypeCurveVolumeRecord,
  VolumeRecord,
} from 'types/api-responses';
import style from './volume-chart.module.scss';
import { MineralType } from 'types/minerals';
import {
  SeriesDragUpdate,
  EnrichedVolumeRecord,
  EnrichedRatioRecord,
  ForecastData,
  XAxisType,
} from '../types';
import {
  convertDateToXAxisValue,
  findFirstProductionDate,
  isOutlierDate,
  isPointOutlier,
  monthOf,
  monthsOnlineAt,
  snapToNearestRecord,
  volumeMap,
} from '../utils';
import { parseMomentUtc } from 'utils/dates';
import { DECIMAL_FORMATTER } from 'lib/ui';
import {
  dailyVolumeToMonthly,
  displayedForecasts,
  displayedProduction,
  pointDateFormat,
  toDailyRate,
} from './volume-display-scaling';
import { X_AXIS_TYPE_LABELS } from './x-axis-switcher';
import { formatNumber } from 'utils/number';
import {
  CHART_EXPORTING_ATTRIBUTE,
  SciChartExportImageButton,
  createSciChartElementHooks,
} from 'components/chart-export';
import { ensureWasmConfigured } from 'components/charts/scichart/ensure-wasm-configured';

// ---------------------------------------------------------------------------
// Staggered rebuild scheduler
// Processes chart rebuilds in small batches across animation frames so that
// switching chart type for 100 charts doesn't block the main thread.
// ---------------------------------------------------------------------------

const CHARTS_PER_FRAME = 4;
const pendingRebuilds = new Map<string, () => void>();
let scheduledRaf: number | null = null;

function flushRebuilds() {
  scheduledRaf = null;
  const entries = Array.from(pendingRebuilds.entries()).slice(
    0,
    CHARTS_PER_FRAME
  );
  for (const [id, fn] of entries) {
    pendingRebuilds.delete(id);
    fn();
  }
  if (pendingRebuilds.size > 0) {
    scheduledRaf = requestAnimationFrame(flushRebuilds);
  }
}

function scheduleChartRebuild(
  chartId: string,
  rebuildFn: () => void
): () => void {
  pendingRebuilds.set(chartId, rebuildFn);
  if (scheduledRaf === null) {
    scheduledRaf = requestAnimationFrame(flushRebuilds);
  }
  return () => {
    pendingRebuilds.delete(chartId);
  };
}

/** The x and y ranges a chart is currently showing. */
type AxisRanges = {
  x: NumberRange | undefined;
  y: Array<{ id: string; range: NumberRange | undefined }>;
};

/**
 * Reads the range every axis is currently showing, so a rebuild can put it
 * back. Returns null before the first render, when there are no axes yet.
 *
 * @param {SciChartSurface} surface - The chart surface.
 * @return {AxisRanges | null} The current ranges, or null if there are none.
 */
function captureRanges(surface: SciChartSurface): AxisRanges | null {
  const xAxis = surface.xAxes.getById(DEFAULT_X_AXIS_ID);
  if (!xAxis?.visibleRange) return null;

  return {
    x: xAxis.visibleRange,
    y: surface.yAxes
      .asArray()
      .map((axis) => ({ id: axis.id, range: axis.visibleRange })),
  };
}

/**
 * Puts back the ranges `captureRanges` read, onto the axes a rebuild created.
 *
 * @param {SciChartSurface} surface - The chart surface.
 * @param {AxisRanges | null} ranges - What to restore, or null to leave the
 *   rebuilt axes as they are.
 * @return {void}
 */
function restoreRanges(
  surface: SciChartSurface,
  ranges: AxisRanges | null
): void {
  if (!ranges) return;

  const xAxis = surface.xAxes.getById(DEFAULT_X_AXIS_ID);
  if (xAxis && ranges.x) xAxis.visibleRange = ranges.x;

  ranges.y.forEach(({ id, range }) => {
    const yAxis = surface.yAxes.getById(id);
    if (yAxis && range) yAxis.visibleRange = range;
  });
}

/** The ratio streams each of the Ratios chart's two y axes carries. */
const PRIMARY_RATIO_STREAMS = ['gor', 'wor'] as const;
const SECONDARY_RATIO_STREAMS = ['wgr', 'cgr'] as const;

/** A record carrying any of the four ratio streams. */
type RatioLike = {
  gor?: number | null;
  wor?: number | null;
  wgr?: number | null;
  cgr?: number | null;
};

/**
 * The 1st to 99th percentile span of the named ratios across every series.
 *
 * A ratio divides one stream by another, so a month where the denominator
 * nearly vanishes produces a value orders of magnitude above the rest. Ranging
 * the axis on the raw minimum and maximum lets one such month flatten
 * everything else onto the floor, so the range comes from the percentiles
 * instead and the extremes fall outside the view.
 *
 * @param {Array} recordSets - Ratio record arrays: production and each forecast.
 * @param {Array} streams - The ratio keys this axis draws.
 * @return {NumberRange} The span, or null when there is too little to measure
 *   and auto-ranging should stand.
 */
function ratioPercentileRange(
  recordSets: RatioLike[][],
  streams: readonly (keyof RatioLike)[]
): NumberRange | null {
  const values: number[] = [];
  for (const records of recordSets) {
    for (const record of records) {
      for (const stream of streams) {
        const value = record[stream];
        if (typeof value === 'number' && Number.isFinite(value)) {
          values.push(value);
        }
      }
    }
  }
  if (values.length === 0) return null;

  values.sort((a, b) => a - b);
  const at = (fraction: number) =>
    values[Math.round(fraction * (values.length - 1))];
  const min = at(0.01);
  const max = at(0.99);

  return max > min ? new NumberRange(min, max) : null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STREAM_COLORS: Record<string, string> = {
  oil: '#42be65',
  water: '#4589ff',
  gas: '#fa4d56',
  gor: '#FF7EB6',
  wor: '#82CFFF',
  wgr: '#BE95FF',
  cgr: '#d2a106',
};

const X_AXIS_CONFIG = {
  cumulativeOil: {
    key: 'cumulativeOil',
    label: 'Cumulative Oil',
    decimals: 0,
    unit: 'BBL',
  },
  cumulativeWater: {
    key: 'cumulativeWater',
    label: 'Cumulative Water',
    decimals: 0,
    unit: 'BBL',
  },
  cumulativeGas: {
    key: 'cumulativeGas',
    label: 'Cumulative Gas',
    decimals: 0,
    unit: 'MCF',
  },
  materialBalanceTimeOil: {
    key: 'materialBalanceTimeOil',
    label: 'Material Balance Time Oil',
    decimals: 2,
    unit: '',
  },
  materialBalanceTimeWater: {
    key: 'materialBalanceTimeWater',
    label: 'Material Balance Time Water',
    decimals: 2,
    unit: '',
  },
  materialBalanceTimeGas: {
    key: 'materialBalanceTimeGas',
    label: 'Material Balance Time Gas',
    decimals: 2,
    unit: '',
  },
} as const;

function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000)
    return `${+(value / 1_000_000_000).toPrecision(3)}B`;
  if (abs >= 1_000_000) return `${+(value / 1_000_000).toPrecision(3)}M`;
  if (abs >= 1_000) return `${+(value / 1_000).toPrecision(3)}k`;
  if (abs >= 1) return `${+value.toPrecision(3)}`;
  if (abs === 0) return '0';
  return value.toPrecision(2);
}

function formatLogLabel(value: number): string {
  if (value <= 0) return '';
  const log = Math.log10(value);
  if (Math.abs(log - Math.round(log)) > 0.01) return '';
  return formatCompact(value);
}

const AXIS_LABEL_STYLE = {
  fontSize: 11,
  padding: new Thickness(0, 6, 0, 0),
};
const AXIS_TITLE_STYLE = { fontSize: 12 };

function sortParallelArraysByX<M>(
  xValues: number[],
  yValues: number[],
  metadata: M[]
): { xValues: number[]; yValues: number[]; metadata: M[] } {
  const indices = xValues
    .map((_, i) => i)
    .sort((a, b) => xValues[a] - xValues[b]);
  return {
    xValues: indices.map((i) => xValues[i]),
    yValues: indices.map((i) => yValues[i]),
    metadata: indices.map((i) => metadata[i]),
  };
}

/** Width-to-height ratio a chart keeps when it is not given a fixed height. */
const CHART_ASPECT_RATIO = 2;

/** Canvas height used for the first paint, before the width is measured. */
const FALLBACK_CHART_HEIGHT = 400;

const HIT_TEST_RADIUS = 10;
const PRIMARY_Y_AXIS_ID = 'primaryYAxis';
const SECONDARY_Y_AXIS_ID = 'secondaryYAxis';
const DEFAULT_X_AXIS_ID = 'defaultXAxis';

// ---------------------------------------------------------------------------
// Point metadata
// ---------------------------------------------------------------------------

interface VolumePointMetadata extends IPointMetadata {
  originalDate: number;
  isOutlier: boolean;
  outlierMarkerColor?: string;
  cumulativeOil?: number;
  cumulativeWater?: number;
  cumulativeGas?: number;
  materialBalanceTimeOil?: number;
  materialBalanceTimeWater?: number;
  materialBalanceTimeGas?: number;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface VolumeChartProps {
  type: 'monthly' | 'logarithmic' | 'cumulative' | 'ratio';
  /**
   * Fixed canvas height in pixels. Without one the canvas keeps a 2:1 shape
   * against whatever width it is given.
   */
  height?: number;
  /** Chart name, rendered in the grey band above the plot. */
  title?: string;
  production: {
    volume: VolumeRecord[];
    cumulative: VolumeRecord[];
    ratio: RatioRecord[];
    materialBalanceTime: VolumeRecord[];
  };
  /**
   * Whether the production volumes are already daily (daily production rather
   * than monthly totals), so the Daily display leaves them as they are.
   */
  productionVolumeIsDailyRate?: boolean;
  typeCurveVolumes: {
    volume: TypeCurveVolumeRecord[];
    cumulative: TypeCurveVolumeRecord[];
    ratio: TypeCurveRatioRecord[];
    materialBalanceTime: TypeCurveVolumeRecord[];
  } | null;
  forecasts: ForecastData[];
  selectedSeries: Array<MineralType | 'gor' | 'wor' | 'wgr' | 'cgr'>;
  isLoading?: boolean;
  showMarkers: {
    segmentation: boolean;
    outlier: boolean;
  };
  segmentDates?: Record<MineralType, string[]>;
  outlierDates?: Record<MineralType, string[]>;
  draggableSeriesConfig?: {
    oil: { offset: number; isXDraggable: boolean; isYDraggable: boolean };
    gas: { offset: number; isXDraggable: boolean; isYDraggable: boolean };
    water: { offset: number; isXDraggable: boolean; isYDraggable: boolean };
    gor: { offset: number; isXDraggable: boolean; isYDraggable: boolean };
    wor: { offset: number; isXDraggable: boolean; isYDraggable: boolean };
    wgr: { offset: number; isXDraggable: boolean; isYDraggable: boolean };
    cgr: { offset: number; isXDraggable: boolean; isYDraggable: boolean };
  };
  onDragEnd: (
    date: Date,
    stream: MineralType | 'gor' | 'wor' | 'wgr' | 'cgr'
  ) => void;
  onDraggableSeriesUpdate: (draggedSeries: SeriesDragUpdate) => void;
  primaryStream?: 'oil' | 'gas' | null;
  showTypeCurve?: boolean;
  /** Controlled x-axis type. If provided, the component won't manage its own state. */
  xAxisType?: XAxisType;
  /** Callback when x-axis type changes (required when xAxisType is controlled). */
  // onXAxisTypeChange?: (value: XAxisType) => void;
  /** Optional footer element (e.g. XAxisSwitcher). Rendered below the chart. */
  footer?: ReactNode;
  /** Optional header element rendered above the chart. Accepts any JSX. */
  header?: ReactNode;
  /** When true, the chart starts zoomed to max(2 * production_months, 50) on the x-axis. */
  initialAutoZoom?: boolean;
  /**
   * Monthly volumes or average daily rates. Only the monthly and log-log
   * charts have a rate to show, so cumulative and ratio ignore it.
   */
  volumeDisplayMode?: 'monthly' | 'daily';
  /**
   * Figures rendered as a strip under the title band. Each carries its own
   * label, so the caller decides what they are.
   */
  headerStats?: Array<{
    label: string;
    value: number | null | undefined;
    unit: string;
  }>;
  /** When provided, renders the icon-only image-export trigger in the header. */
  exportConfig?: {
    label: string;
    filenamePrefix: string;
    /** Class on the download-icon button — e.g. to override its fill color. */
    iconClassName?: string;
  };
}

// ---------------------------------------------------------------------------
// Custom chart modifier: DataPointDragModifier
// Handles dragging of forecast data points in-place.
// ---------------------------------------------------------------------------

type DragEndCallback = (update: SeriesDragUpdate) => void;

interface DraggableSeriesEntry {
  seriesId: string;
  stream: string;
  isXDraggable: boolean;
  isYDraggable: boolean;
}

class DataPointDragModifier extends ChartModifierBase2D {
  readonly type = 'DataPointDragModifier';
  private draggingSeries: IRenderableSeries | null = null;
  private draggingIndex: number = -1;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  // Every point as it stood when the drag began, so each mouse move can
  // reposition the whole curve from the original rather than compounding.
  private dragStartPoints: Array<{ x: number; y: number }> = [];
  private draggableEntries: DraggableSeriesEntry[] = [];
  private onDragEndCallback: DragEndCallback | null = null;
  private earliestDate: moment.Moment = moment.utc();
  private xAxisType: XAxisType = 'date';

  setConfig(
    entries: DraggableSeriesEntry[],
    callback: DragEndCallback,
    earliestDate: moment.Moment,
    xAxisType: XAxisType
  ) {
    this.draggableEntries = entries;
    this.onDragEndCallback = callback;
    this.earliestDate = earliestDate;
    this.xAxisType = xAxisType;
  }

  override modifierMouseDown(args: ModifierMouseArgs): void {
    super.modifierMouseDown(args);
    if (!this.parentSurface) return;

    const seriesCollection = this.parentSurface.renderableSeries.asArray();
    for (const entry of this.draggableEntries) {
      const series = seriesCollection.find((s) => s.id === entry.seriesId);
      if (!series || !series.isVisible) continue;

      const hitTest = series.hitTestProvider.hitTest(
        args.mousePoint.x,
        args.mousePoint.y,
        HIT_TEST_RADIUS
      );
      if (hitTest.isHit) {
        this.draggingSeries = series;
        this.draggingIndex = hitTest.dataSeriesIndex;
        const ds = series.dataSeries as XyDataSeries;
        this.dragStartX = ds.getNativeXValues().get(this.draggingIndex);
        this.dragStartY = ds.getNativeYValues().get(this.draggingIndex);

        const xValues = ds.getNativeXValues();
        const yValues = ds.getNativeYValues();
        this.dragStartPoints = [];
        for (let index = 0; index < ds.count(); index += 1) {
          this.dragStartPoints.push({
            x: xValues.get(index),
            y: yValues.get(index),
          });
        }

        args.handled = true;
        return;
      }
    }
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    if (!this.draggingSeries || this.draggingIndex < 0) return;
    if (!this.parentSurface) return;

    const entry = this.draggableEntries.find(
      (e) => e.seriesId === this.draggingSeries!.id
    );
    if (!entry) return;

    const xAxis = this.parentSurface.xAxes.getById(DEFAULT_X_AXIS_ID);
    const yAxisId =
      entry.stream === 'wgr' || entry.stream === 'cgr'
        ? SECONDARY_Y_AXIS_ID
        : PRIMARY_Y_AXIS_ID;
    const yAxis = this.parentSurface.yAxes.getById(yAxisId);
    if (!xAxis || !yAxis) return;

    const xCalc = xAxis.getCurrentCoordinateCalculator();
    const yCalc = yAxis.getCurrentCoordinateCalculator();

    const ds = this.draggingSeries.dataSeries as XyDataSeries;
    const newX = entry.isXDraggable
      ? xCalc.getDataValue(args.mousePoint.x)
      : this.dragStartX;
    const newY = entry.isYDraggable
      ? yCalc.getDataValue(args.mousePoint.y)
      : this.dragStartY;

    // The whole curve moves, because that is what the drag means: dragging up
    // multiplies qi, which lifts every month with it, and dragging sideways
    // shifts the start. Moving only the grabbed point put a kink in the curve
    // that snapped straight on release, once the model recomputed.
    const yScale =
      this.dragStartY !== 0 && Number.isFinite(newY / this.dragStartY)
        ? newY / this.dragStartY
        : 1;
    const xShift = newX - this.dragStartX;

    this.dragStartPoints.forEach((point, index) => {
      ds.updateXy(index, point.x + xShift, point.y * yScale);
    });

    args.handled = true;
  }

  override modifierMouseUp(args: ModifierMouseArgs): void {
    super.modifierMouseUp(args);
    if (!this.draggingSeries || this.draggingIndex < 0) return;

    const entry = this.draggableEntries.find(
      (e) => e.seriesId === this.draggingSeries!.id
    );
    if (!entry || !this.onDragEndCallback) {
      this.resetDrag();
      return;
    }

    const ds = this.draggingSeries.dataSeries as XyDataSeries;
    const newX = ds.getNativeXValues().get(this.draggingIndex);
    const newY = ds.getNativeYValues().get(this.draggingIndex);

    // Convert X values to months from earliest date if xAxisType is 'date'
    let reportOldX = this.dragStartX;
    let reportNewX = newX;
    if (this.xAxisType === 'date') {
      reportOldX = moment
        .utc(this.dragStartX)
        .diff(this.earliestDate, 'months', true);
      reportNewX = moment.utc(newX).diff(this.earliestDate, 'months', true);
    }

    this.onDragEndCallback({
      oldX: reportOldX,
      newX: reportNewX,
      oldY: this.dragStartY,
      newY,
      stream: entry.stream as MineralType,
    });

    this.resetDrag();
    args.handled = true;
  }

  private resetDrag() {
    this.dragStartPoints = [];
    this.draggingSeries = null;
    this.draggingIndex = -1;
  }
}

// ---------------------------------------------------------------------------
// Tooltip types and helpers
// ---------------------------------------------------------------------------

interface TooltipHitData {
  seriesId: string;
  seriesName: string;
  stroke: string;
  yValue: number;
  dataSeriesIndex: number;
  dataSeries: XyDataSeries;
}

/** How the tooltip shows a point: the chart it is on and how to format it. */
interface TooltipFormat {
  chartType: 'monthly' | 'logarithmic' | 'cumulative' | 'ratio';
  xAxisType: XAxisType;
  decimalsForChart: number;
  /** A moment format for the point's date: with the day for daily rows, month and year otherwise. */
  dateFormat: string;
  /**
   * The date of the well's first production row, in any stream, as a timestamp.
   * Months online count from it, as in the forecast table. Null when the chart
   * has no production, and they then count from the series' first point.
   */
  firstProductionDate: number | null;
}

/**
 * The tooltip for the points under the mouse.
 *
 * @param {TooltipHitData[]} hits - The points under the mouse, one per series.
 * @param {TooltipFormat} format - The chart and how to format its values.
 * @return {string} The tooltip HTML, or an empty string when there is nothing to show.
 */
function buildTooltipHtml(
  hits: TooltipHitData[],
  {
    chartType,
    xAxisType,
    decimalsForChart,
    dateFormat,
    firstProductionDate,
  }: TooltipFormat
): string {
  if (hits.length === 0) return '';

  // Find a hit with metadata for date/cumulative info
  let mainHit: TooltipHitData | undefined;
  for (const hit of hits) {
    if (hit.dataSeriesIndex >= 0) {
      const meta = hit.dataSeries.getMetadataAt(hit.dataSeriesIndex) as
        VolumePointMetadata | undefined;
      if (meta?.originalDate) {
        mainHit = hit;
        break;
      }
    }
  }
  if (!mainHit) return '';

  const meta = mainHit.dataSeries.getMetadataAt(
    mainHit.dataSeriesIndex
  ) as VolumePointMetadata;
  if (!meta) return '';

  const date = moment.utc(meta.originalDate).format(dateFormat);

  const firstMeta = mainHit.dataSeries.getMetadataAt(0) as
    VolumePointMetadata | undefined;
  const monthsOnline = monthsOnlineAt(
    meta.originalDate,
    firstProductionDate,
    firstMeta?.originalDate ?? meta.originalDate
  );

  // Extra info (cumulative / MBT) when on that x-axis type
  let extraInfo = '';
  const config = X_AXIS_CONFIG[xAxisType as keyof typeof X_AXIS_CONFIG];
  if (config && meta[config.key as keyof VolumePointMetadata] !== null) {
    const value = meta[config.key as keyof VolumePointMetadata] as number;
    const formatted = formatNumber({ value, minPrecision: decimalsForChart });
    extraInfo = `<br/>${config.label}: <b>${formatted}${
      config.unit ? ` ${config.unit}` : ''
    }</b>`;
  }

  const pointsHtml = hits
    .map((hit) => {
      // Skip outlier scatter overlays in tooltip
      if (hit.seriesId.startsWith('outlier-')) return '';

      const yVal = hit.yValue;
      if (yVal === null) return '';

      // Format display name
      let displayName = hit.seriesName || hit.seriesId;
      if (hit.seriesId.startsWith('type-curve-')) {
        displayName = `Type Curve ${hit.seriesId.replace('type-curve-', '')}`;
      }

      // Uppercase ratio suffixes
      if (chartType === 'ratio') {
        const last3 = displayName.slice(-3);
        if (['gor', 'wor', 'wgr', 'cgr'].includes(last3)) {
          displayName = displayName.slice(0, -3) + last3.toUpperCase();
        }
      }

      const formatted = formatNumber({
        value: yVal,
        minPrecision: decimalsForChart,
      });

      // Determine unit suffix
      const stream = hit.seriesId.split('-').pop() ?? '';
      let suffix = '';
      if (chartType === 'ratio') {
        if (stream === 'gor') suffix = ' MCF/BBL';
        else if (stream === 'wor') suffix = ' BBL/BBL';
        else if (stream === 'wgr' || stream === 'cgr') suffix = ' BBL/MMCF';
      } else {
        suffix = stream === 'gas' ? ' MCF' : ' BBL';
      }

      return `<br/><span style="color:${hit.stroke}">\u25CF</span> ${displayName}: <b>${formatted}${suffix}</b>`;
    })
    .join('');

  return `<div style="background:rgba(255,255,255,0.95);border:1px solid #ccc;padding:8px;border-radius:4px;font-size:12px;pointer-events:none;">
    <b>${date}</b><br/>Months Online: <b>${monthsOnline}</b>${extraInfo}${pointsHtml}
  </div>`;
}

// ---------------------------------------------------------------------------
// Custom chart modifier: TooltipChartModifier
// Performs hit-testing across all visible series and renders a shared tooltip.
// ---------------------------------------------------------------------------

interface TooltipConfig extends TooltipFormat {
  tooltipElement: HTMLDivElement | null;
  containerElement: HTMLDivElement | null;
}

class TooltipChartModifier extends ChartModifierBase2D {
  readonly type = 'TooltipChartModifier';
  private config: TooltipConfig = {
    tooltipElement: null,
    containerElement: null,
    chartType: 'monthly',
    xAxisType: 'date',
    decimalsForChart: 0,
    dateFormat: pointDateFormat(false),
    firstProductionDate: null,
  };

  setConfig(cfg: TooltipConfig) {
    this.config = cfg;
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    const { tooltipElement, containerElement } = this.config;
    if (!tooltipElement || !this.parentSurface) return;

    // Hit-test all visible series at the mouse X position
    const hits: TooltipHitData[] = [];
    for (const series of this.parentSurface.renderableSeries.asArray()) {
      if (!series.isVisible) continue;
      const hitTest = series.hitTestProvider.hitTestXSlice(
        args.mousePoint.x,
        args.mousePoint.y,
        HIT_TEST_RADIUS
      );
      if (hitTest.isHit && hitTest.dataSeriesIndex >= 0) {
        hits.push({
          seriesId: series.id ?? '',
          seriesName: (series.dataSeries as XyDataSeries)?.dataSeriesName ?? '',
          stroke: (series as FastLineRenderableSeries).stroke ?? '#333',
          yValue: hitTest.yValue,
          dataSeriesIndex: hitTest.dataSeriesIndex,
          dataSeries: series.dataSeries as XyDataSeries,
        });
      }
    }

    if (hits.length === 0) {
      tooltipElement.style.display = 'none';
      return;
    }

    const html = buildTooltipHtml(hits, this.config);
    if (!html) {
      tooltipElement.style.display = 'none';
      return;
    }

    tooltipElement.innerHTML = html;
    tooltipElement.style.display = 'block';

    if (containerElement) {
      const maxX =
        containerElement.offsetWidth - tooltipElement.offsetWidth - 5;
      const maxY =
        containerElement.offsetHeight - tooltipElement.offsetHeight - 5;
      tooltipElement.style.left = `${Math.min(args.mousePoint.x + 15, maxX)}px`;
      tooltipElement.style.top = `${Math.max(
        0,
        Math.min(args.mousePoint.y - 10, maxY)
      )}px`;
    }
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    if (this.config.tooltipElement) {
      this.config.tooltipElement.style.display = 'none';
    }
  }
}

/**
 * Formats a headerStats figure as a whole number, or `--` when there is none.
 *
 * @param {number | null | undefined} value - The figure.
 * @return {string} The text shown for it.
 */
function formatHeaderStat(value: number | null | undefined): string {
  return value === null || value === undefined
    ? '--'
    : formatNumber({ value, minPrecision: 0, maxPrecision: 0 });
}

// ---------------------------------------------------------------------------
// VolumeChart2 Component
// ---------------------------------------------------------------------------

export const VolumeChart2 = ({
  type,
  height: fixedHeight,
  title,
  production,
  productionVolumeIsDailyRate = false,
  forecasts = [],
  selectedSeries,
  isLoading,
  segmentDates,
  showMarkers,
  draggableSeriesConfig,
  onDragEnd,
  onDraggableSeriesUpdate,
  outlierDates,
  primaryStream,
  typeCurveVolumes,
  showTypeCurve = false,
  xAxisType: controlledXAxisType,
  footer,
  header,
  initialAutoZoom = false,
  volumeDisplayMode = 'monthly',
  headerStats,
  exportConfig,
}: VolumeChartProps) => {
  const [internalXAxisType] = useState<XAxisType>(
    type === 'logarithmic' ? 'count' : 'date'
  );
  const xAxisType = controlledXAxisType ?? internalXAxisType;
  // Measured by the resize observer below. Until it has run the canvas takes
  // the fallback height, which the first observation replaces.
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  const height =
    fixedHeight ??
    (measuredWidth === null
      ? FALLBACK_CHART_HEIGHT
      : Math.round(measuredWidth / CHART_ASPECT_RATIO));
  const [isInitialized, setIsInitialized] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  // Read inside the rebuild, which is not re-created when this changes.
  const isZoomedRef = useRef(false);
  isZoomedRef.current = isZoomed;
  const decimalsForChart = type === 'ratio' ? 2 : 0;

  const surfaceRef = useRef<SciChartSurface | null>(null);
  const wasmContextRef = useRef<TSciChart | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // The SciChart canvas div (chartDivId) is the export capture target.
  const canvasDivRef = useRef<HTMLDivElement>(null);
  // The headerStats figures, drawn under the title in the exported image.
  const exportSubtitle = useMemo(
    () =>
      headerStats?.map(
        ({ label, value, unit }) =>
          `${label} ${formatHeaderStat(value)} ${unit}`
      ),
    [headerStats]
  );
  // Scale axis-label/title fonts + series/marker weight to the export resolution.
  const exportElementHooks = useMemo(
    () =>
      createSciChartElementHooks(() => surfaceRef.current, {
        // Show the x-axis title in the export only (it's intentionally omitted
        // from the live chart, which has the x-axis switcher in the footer).
        getExportAxisTitles: () => [
          { axisId: DEFAULT_X_AXIS_ID, title: X_AXIS_TYPE_LABELS[xAxisType] },
        ],
      }),
    [xAxisType]
  );
  const chartDivId = useRef(
    `volume-chart-2-${Math.random().toString(36).slice(2, 11)}`
  ).current;

  // Tooltip overlay ref
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // The x range the chart opens on, which is what Reset zoom goes back to and
  // what "the user has zoomed" is judged against. It is the initialAutoZoom
  // window where there is one, and the full extent of the data otherwise.
  const baselineXRangeRef = useRef<NumberRange | null>(null);

  // What the axes measured at the last rebuild: the x-axis option, and whether
  // volumes were daily rates. A zoom is only kept while these stay the same,
  // since a range in one unit (dates, months, barrels) means nothing in another.
  const axisUnitsRef = useRef<string | null>(null);

  // Y ranges the rebuild pins by hand: the log chart's decade-snapped range and
  // the ratio chart's percentile range. `zoomExtents` re-ranges every axis from
  // the raw data, so these have to be put back after each call.
  const yRangeOverridesRef = useRef<
    Array<{ axisId: string; range: NumberRange }>
  >([]);

  const applyYRangeOverrides = useCallback((surface: SciChartSurface) => {
    const overridden = new Set<string>();

    for (const { axisId, range } of yRangeOverridesRef.current) {
      const axis = surface.yAxes.getById(axisId);
      if (!axis) continue;

      axis.visibleRange = range;
      overridden.add(axisId);
    }

    // Auto-ranging pads below the lowest point, and no volume goes negative.
    for (const axis of surface.yAxes.asArray()) {
      if (overridden.has(axis.id)) continue;

      const range = axis.visibleRange;
      if (range && range.min < 0) {
        axis.visibleRange = new NumberRange(0, range.max);
      }
    }
  }, []);

  /** Returns the chart to the view it opened on. */
  const resetToBaseline = useCallback(
    (surface: SciChartSurface) => {
      surface.zoomExtents();

      const xAxis = surface.xAxes.getById(DEFAULT_X_AXIS_ID);
      if (xAxis && baselineXRangeRef.current) {
        xAxis.visibleRange = baselineXRangeRef.current;
      }

      applyYRangeOverrides(surface);
      setIsZoomed(false);
    },
    [applyYRangeOverrides]
  );

  // -----------------------------------------------------------------------
  // Data enrichment helpers (same logic as original)
  // -----------------------------------------------------------------------

  const enrichVolumeRecord = useCallback(
    (
      baseArray: VolumeRecord[] | RatioRecord[],
      cumulativeArray: VolumeRecord[],
      mbtArray: VolumeRecord[]
    ) => {
      // Looked up by date: daily production has a row per day.
      const cumulativeByDate = new Map(
        cumulativeArray.map((cumRecord) => [cumRecord.date, cumRecord])
      );
      const mbtByDate = new Map(
        mbtArray.map((mbtRecord) => [mbtRecord.date, mbtRecord])
      );
      return baseArray.map((baseRecord) => {
        const matchingCumulative = cumulativeByDate.get(baseRecord.date);
        const matchingMBT = mbtByDate.get(baseRecord.date);
        return {
          ...baseRecord,
          cumulativeOil: matchingCumulative?.oil,
          cumulativeWater: matchingCumulative?.water,
          cumulativeGas: matchingCumulative?.gas,
          materialBalanceTimeOil: matchingMBT?.oil,
          materialBalanceTimeWater: matchingMBT?.water,
          materialBalanceTimeGas: matchingMBT?.gas,
        };
      });
    },
    []
  );

  // Only the monthly and log-log charts plot a rate, so the daily toggle
  // applies to those two. Scaling here, where raw volumes enter the chart,
  // means the series, tooltips, markers, and drag annotations all show the
  // same numbers. The drag callback converts back to monthly volumes.
  const dailyScale =
    (type === 'monthly' || type === 'logarithmic') &&
    volumeDisplayMode === 'daily';

  const scaledProduction = useMemo(
    () =>
      displayedProduction(
        production,
        dailyScale,
        Boolean(productionVolumeIsDailyRate)
      ),
    [production, dailyScale, productionVolumeIsDailyRate]
  );

  const scaledForecasts = useMemo(
    () => displayedForecasts(forecasts, dailyScale),
    [forecasts, dailyScale]
  );

  // The chart plots daily rates while the forecast model works in monthly
  // volumes, so a drag has to be reported back in monthly terms.
  const reportDragUpdate = useCallback(
    (update: SeriesDragUpdate) => {
      if (!dailyScale) {
        onDraggableSeriesUpdate(update);
        return;
      }
      onDraggableSeriesUpdate({
        ...update,
        oldY: dailyVolumeToMonthly(update.oldY),
        newY: dailyVolumeToMonthly(update.newY),
      });
    },
    [dailyScale, onDraggableSeriesUpdate]
  );

  const enrichedProduction = useMemo(
    () => ({
      volume: enrichVolumeRecord(
        scaledProduction.volume,
        production.cumulative,
        production.materialBalanceTime
      ) as EnrichedVolumeRecord[],
      cumulative: enrichVolumeRecord(
        production.cumulative,
        production.cumulative,
        production.materialBalanceTime
      ) as EnrichedVolumeRecord[],
      ratio: enrichVolumeRecord(
        production.ratio,
        production.cumulative,
        production.materialBalanceTime
      ) as EnrichedRatioRecord[],
      materialBalanceTime: enrichVolumeRecord(
        production.materialBalanceTime,
        production.cumulative,
        production.materialBalanceTime
      ) as EnrichedVolumeRecord[],
    }),
    [production, scaledProduction, enrichVolumeRecord]
  );

  const enrichedForecasts = useMemo(
    () =>
      scaledForecasts.map((forecast) => ({
        id: forecast.id,
        name: forecast.name,
        isDraggable: forecast.isDraggable ?? false,
        lineStyle: forecast.lineStyle ?? 'dashed',
        volume: enrichVolumeRecord(
          forecast.volume,
          forecast.cumulative,
          forecast.materialBalanceTime
        ) as EnrichedVolumeRecord[],
        cumulative: enrichVolumeRecord(
          forecast.cumulative,
          forecast.cumulative,
          forecast.materialBalanceTime
        ) as EnrichedVolumeRecord[],
        ratio: enrichVolumeRecord(
          forecast.ratio,
          forecast.cumulative,
          forecast.materialBalanceTime
        ) as EnrichedRatioRecord[],
        materialBalanceTime: enrichVolumeRecord(
          forecast.materialBalanceTime,
          forecast.cumulative,
          forecast.materialBalanceTime
        ) as EnrichedVolumeRecord[],
      })),
    [scaledForecasts, enrichVolumeRecord]
  );

  // With daily production, the first of the earliest month, so months online
  // count whole calendar months and a dragged start date lands on a month.
  const earliestDate = useMemo(() => {
    const allDates = [
      ...enrichedProduction[volumeMap[type]].map((r) => r.date),
      ...enrichedForecasts.flatMap((forecast) =>
        forecast[volumeMap[type]].map((r) => r.date)
      ),
    ].filter((date) => date);
    if (allDates.length === 0) return moment.utc();

    const earliest = moment.utc(
      Math.min(...allDates.map((d) => moment.utc(d).valueOf()))
    );
    return productionVolumeIsDailyRate ? earliest.startOf('month') : earliest;
  }, [
    enrichedProduction,
    enrichedForecasts,
    type,
    productionVolumeIsDailyRate,
  ]);

  const earliestProductionDate = useMemo(() => {
    const first = findFirstProductionDate(enrichedProduction[volumeMap[type]]);
    return first === null ? moment.utc() : moment.utc(first);
  }, [enrichedProduction, type]);

  // From the volume rows whatever this chart draws, so every chart and the
  // forecast table count months online from the same month.
  const firstProductionDate = useMemo(
    () => findFirstProductionDate(enrichedProduction.volume),
    [enrichedProduction]
  );

  const enrichedTypeCurveData = useMemo(() => {
    if (!typeCurveVolumes || typeCurveVolumes.volume.length === 0) return [];
    const volumesData = typeCurveVolumes.volume;
    const cumulativesData = typeCurveVolumes.cumulative;
    const ratiosData = typeCurveVolumes.ratio;
    const mbtsData = typeCurveVolumes.materialBalanceTime;

    return volumesData.map(
      (volumeRecord: TypeCurveVolumeRecord, index: number) => {
        const date = moment
          .utc(earliestProductionDate)
          .add(volumeRecord.month - 1, 'months');
        const cumulativeRecord = cumulativesData[index];
        const ratioRecord = ratiosData[index];
        const mbtRecord = mbtsData[index];
        // The type curve is in monthly volumes, scaled like the production
        // and forecasts above.
        const rate = dailyScale ? toDailyRate(volumeRecord) : volumeRecord;
        return {
          date: date.toDate(),
          oil: rate.oil ?? 0,
          gas: rate.gas ?? 0,
          water: rate.water ?? 0,
          cumulativeOil: cumulativeRecord?.oil ?? 0,
          cumulativeGas: cumulativeRecord?.gas ?? 0,
          cumulativeWater: cumulativeRecord?.water ?? 0,
          materialBalanceTimeOil: mbtRecord?.oil ?? 0,
          materialBalanceTimeGas: mbtRecord?.gas ?? 0,
          materialBalanceTimeWater: mbtRecord?.water ?? 0,
          gor: ratioRecord?.gor ?? 0,
          wor: ratioRecord?.wor ?? 0,
          wgr: ratioRecord?.wgr ?? 0,
          cgr: ratioRecord?.cgr ?? 0,
        };
      }
    );
  }, [typeCurveVolumes, earliestProductionDate, dailyScale]);

  // -----------------------------------------------------------------------
  // X-value calculation helper
  // -----------------------------------------------------------------------

  const getXValue = useCallback(
    (
      record: EnrichedVolumeRecord | EnrichedRatioRecord,
      axisType: XAxisType,
      refDate: moment.Moment
    ): number => {
      if (axisType === 'date') return moment.utc(record[axisType]).valueOf();
      // With daily production, fractional, so the days of a month spread
      // across it rather than stacking on one month.
      if (axisType === 'count')
        return (
          moment
            .utc(record.date)
            .diff(refDate, 'months', productionVolumeIsDailyRate) + 1
        );
      return record[axisType];
    },
    [productionVolumeIsDailyRate]
  );

  // -----------------------------------------------------------------------
  // SciChart surface initialization (once)
  // -----------------------------------------------------------------------

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        ensureWasmConfigured();

        const theme = new SciChartJSLightTheme();
        theme.sciChartBackground = '#FFFFFF';
        theme.majorGridLineBrush = '#d0d0d0';
        theme.minorGridLineBrush = '#e8e8e8';
        theme.axisBandsFill = 'transparent';

        const { sciChartSurface, wasmContext } = await SciChartSurface.create(
          chartDivId,
          {
            theme,
            padding: new Thickness(10, 10, 10, 10),
          }
        );

        if (!isMounted) {
          sciChartSurface.delete();
          return;
        }
        surfaceRef.current = sciChartSurface;
        wasmContextRef.current = wasmContext;
        setIsInitialized(true);
      } catch (err) {
        console.error('SciChart initialization failed:', err);
      }
    };

    init();

    return () => {
      isMounted = false;
      surfaceRef.current?.delete();
      surfaceRef.current = null;
      wasmContextRef.current = null;
    };
  }, [chartDivId]);

  // -----------------------------------------------------------------------
  // Rebuild chart content when data or settings change
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!isInitialized) return;
    if (!surfaceRef.current || !wasmContextRef.current) return;

    const cancelRebuild = scheduleChartRebuild(chartDivId, () => {
      const surface = surfaceRef.current;
      const wasm = wasmContextRef.current;
      if (!surface || !wasm) return;

      surface.suspendUpdates();

      // Changing a parameter runs this whole rebuild, which replaces the axes
      // and so throws away wherever the user had zoomed or panned to. Their
      // range is kept here and put back at the end. Only when they have
      // actually zoomed: otherwise auto-ranging should keep following the data.
      const axisUnits = `${xAxisType}|${dailyScale}|${productionVolumeIsDailyRate}`;
      const sameAxisUnits = axisUnitsRef.current === axisUnits;
      axisUnitsRef.current = axisUnits;
      const keptRanges =
        isZoomedRef.current && sameAxisUnits ? captureRanges(surface) : null;

      // Clear existing content
      surface.renderableSeries.clear();
      surface.xAxes.clear();
      surface.yAxes.clear();
      surface.annotations.clear();
      surface.chartModifiers.clear();

      // =================================================================
      // 1. Build axes
      // =================================================================

      const isLogXAxis = type === 'logarithmic' && xAxisType !== 'date';
      const isDateXAxis = xAxisType === 'date';

      if (isLogXAxis) {
        const logXLabelProvider = new NumericLabelProvider();
        logXLabelProvider.formatLabel = formatLogLabel;
        surface.xAxes.add(
          new LogarithmicAxis(wasm, {
            id: DEFAULT_X_AXIS_ID,
            axisAlignment: EAxisAlignment.Bottom,
            autoRange: EAutoRange.Once,
            drawMajorGridLines: true,
            drawMinorGridLines: false,
            labelProvider: logXLabelProvider,
            labelStyle: AXIS_LABEL_STYLE,
          })
        );
      } else {
        const xAxisOptions: ConstructorParameters<typeof NumericAxis>[1] = {
          id: DEFAULT_X_AXIS_ID,
          axisAlignment: EAxisAlignment.Bottom,
          autoRange: EAutoRange.Once,
          drawMajorGridLines: true,
          drawMinorGridLines: false,
          labelStyle: AXIS_LABEL_STYLE,
        };

        if (isDateXAxis) {
          const dateLabelProvider = new NumericLabelProvider();
          dateLabelProvider.formatLabel = (dataValue: number) =>
            moment.utc(dataValue).format('MMM YYYY');
          dateLabelProvider.formatCursorLabel = (dataValue: number) =>
            moment
              .utc(dataValue)
              .format(pointDateFormat(Boolean(productionVolumeIsDailyRate)));
          xAxisOptions.labelProvider = dateLabelProvider;
        } else {
          const numberLabelProvider = new NumericLabelProvider();
          numberLabelProvider.formatLabel = (dataValue: number) =>
            DECIMAL_FORMATTER.format(dataValue);
          xAxisOptions.labelProvider = numberLabelProvider;
        }

        const xAxis = new NumericAxis(wasm, xAxisOptions);
        if (isDateXAxis) {
          xAxis.tickProvider = new MonthlyTickProvider(wasm);
        }
        surface.xAxes.add(xAxis);
      }

      const isLogYAxis = type === 'monthly' || type === 'logarithmic';
      const yGrowBy = new NumberRange(0.05, 0.1);

      // Ranges this rebuild pins by hand, collected as the axes are built.
      const yRangeOverrides: Array<{ axisId: string; range: NumberRange }> = [];

      // Pre-compute log-cycle-snapped Y range for clean gridlines. The forecast
      // counts towards the scale as well as the production: a forecast that
      // runs past what was ever reported still has to fit on the axis.
      let yLogMin = 1;
      let yLogMax = 1e6;
      if (isLogYAxis) {
        let lowest = Infinity;
        let highest = -Infinity;
        const logSets = [
          enrichedProduction[volumeMap[type]],
          ...enrichedForecasts.map((forecast) => forecast[volumeMap[type]]),
        ];
        for (const records of logSets) {
          for (const record of records) {
            for (const stream of ['oil', 'gas', 'water'] as const) {
              const yVal = record[stream as keyof typeof record] as number;
              if (typeof yVal !== 'number' || !(yVal > 0)) continue;
              if (yVal < lowest) lowest = yVal;
              if (yVal > highest) highest = yVal;
            }
          }
        }
        if (highest > 0) {
          yLogMin = Math.pow(10, Math.floor(Math.log10(lowest)));
          yLogMax = Math.pow(10, Math.ceil(Math.log10(highest)));
        }
        while (Math.log10(yLogMax / yLogMin) < 3) yLogMin /= 10;
        yRangeOverrides.push({
          axisId: PRIMARY_Y_AXIS_ID,
          range: new NumberRange(yLogMin, yLogMax),
        });
      }

      const makeLabelProvider = (formatter: (v: number) => string) => {
        const lp = new NumericLabelProvider();
        lp.formatLabel = formatter;
        return lp;
      };

      const yAxisBase = {
        axisAlignment: EAxisAlignment.Left,
        autoRange: EAutoRange.Once,
        drawMajorGridLines: true,
        labelStyle: AXIS_LABEL_STYLE,
        axisTitleStyle: AXIS_TITLE_STYLE,
      } as const;

      if (type === 'ratio') {
        const ratioSets: RatioLike[][] = [
          enrichedProduction.ratio,
          ...enrichedForecasts.map((forecast) => forecast.ratio),
        ];
        // An explicit range replaces auto-ranging; without Never the axis would
        // range on the raw extremes again.
        const rangeFor = (
          axisId: string,
          streams: readonly (keyof RatioLike)[]
        ) => {
          const range = ratioPercentileRange(ratioSets, streams);
          if (!range) return {};

          yRangeOverrides.push({ axisId, range });
          return { autoRange: EAutoRange.Never, visibleRange: range };
        };

        surface.yAxes.add(
          new NumericAxis(wasm, {
            ...yAxisBase,
            id: PRIMARY_Y_AXIS_ID,
            axisTitle: 'GOR / WOR',
            drawMinorGridLines: false,
            growBy: yGrowBy,
            labelProvider: makeLabelProvider(formatCompact),
            ...rangeFor(PRIMARY_Y_AXIS_ID, PRIMARY_RATIO_STREAMS),
          })
        );
        surface.yAxes.add(
          new NumericAxis(wasm, {
            ...yAxisBase,
            id: SECONDARY_Y_AXIS_ID,
            axisAlignment: EAxisAlignment.Right,
            axisTitle: 'WGR / CGR',
            drawMajorGridLines: false,
            drawMinorGridLines: false,
            growBy: yGrowBy,
            labelProvider: makeLabelProvider(formatCompact),
            ...rangeFor(SECONDARY_Y_AXIS_ID, SECONDARY_RATIO_STREAMS),
          })
        );
      } else if (isLogYAxis) {
        const numDecades = Math.round(
          Math.log10(yLogMax) - Math.log10(yLogMin)
        );
        surface.yAxes.add(
          new LogarithmicAxis(wasm, {
            ...yAxisBase,
            id: PRIMARY_Y_AXIS_ID,
            axisTitle: 'oil / gas / water',
            drawMinorGridLines: true,
            visibleRange: new NumberRange(yLogMin, yLogMax),
            maxAutoTicks: numDecades + 1,
            minorsPerMajor: 9,
            labelProvider: makeLabelProvider(formatLogLabel),
          })
        );
      } else {
        surface.yAxes.add(
          new NumericAxis(wasm, {
            ...yAxisBase,
            id: PRIMARY_Y_AXIS_ID,
            axisTitle: 'oil / gas / water',
            drawMinorGridLines: false,
            growBy: yGrowBy,
            labelProvider: makeLabelProvider(formatCompact),
          })
        );
      }

      yRangeOverridesRef.current = yRangeOverrides;

      // =================================================================
      // 2. Build production series
      // =================================================================

      const productionStreams =
        type === 'ratio'
          ? ['gor', 'wor', 'wgr', 'cgr']
          : ['oil', 'gas', 'water'];

      const productionData = enrichedProduction[volumeMap[type]];

      for (const stream of productionStreams) {
        const xValues: number[] = [];
        const yValues: number[] = [];
        const metadata: VolumePointMetadata[] = [];
        const outlierXValues: number[] = [];
        const outlierYValues: number[] = [];
        const outlierMeta: VolumePointMetadata[] = [];

        for (const record of productionData) {
          const yVal = record[stream as keyof typeof record] as number;
          const xVal = getXValue(record, xAxisType, earliestDate);
          if (xVal === null || yVal === null) continue;
          if (isLogYAxis && yVal <= 0) continue;

          const isOutlier = isPointOutlier(
            'Production',
            stream,
            type,
            record.date,
            outlierDates,
            productionVolumeIsDailyRate
          );

          const pointMeta: VolumePointMetadata = {
            isSelected: false,
            originalDate: moment.utc(record.date).valueOf(),
            isOutlier,
            cumulativeOil: record.cumulativeOil,
            cumulativeWater: record.cumulativeWater,
            cumulativeGas: record.cumulativeGas,
            materialBalanceTimeOil: record.materialBalanceTimeOil,
            materialBalanceTimeWater: record.materialBalanceTimeWater,
            materialBalanceTimeGas: record.materialBalanceTimeGas,
          };

          xValues.push(xVal);
          yValues.push(yVal);
          metadata.push(pointMeta);

          // Build outlier overlay data
          if (isOutlier && showMarkers?.outlier) {
            let markerColor = STREAM_COLORS[stream];
            if (type === 'ratio' && outlierDates) {
              const dateStr = moment.utc(record.date).format('YYYY-MM-DD');
              /**
               * Whether this point is one of a stream's outliers.
               *
               * @param {string[] | undefined} dates - The stream's outlier dates.
               * @return {boolean} True when it is.
               */
              const isStreamOutlier = (dates: string[] | undefined) =>
                isOutlierDate(dates, dateStr, productionVolumeIsDailyRate);
              if (stream === 'gor') {
                markerColor = isStreamOutlier(outlierDates.gas)
                  ? STREAM_COLORS.gas
                  : STREAM_COLORS.oil;
              } else if (stream === 'wor') {
                markerColor = isStreamOutlier(outlierDates.water)
                  ? STREAM_COLORS.water
                  : STREAM_COLORS.oil;
              } else if (stream === 'wgr') {
                markerColor = isStreamOutlier(outlierDates.water)
                  ? STREAM_COLORS.water
                  : STREAM_COLORS.gas;
              } else if (stream === 'cgr') {
                markerColor = isStreamOutlier(outlierDates.oil)
                  ? STREAM_COLORS.oil
                  : STREAM_COLORS.gas;
              }
            }
            outlierXValues.push(xVal);
            outlierYValues.push(yVal);
            outlierMeta.push({ ...pointMeta, outlierMarkerColor: markerColor });
          }
        }

        if (xValues.length === 0) continue;

        const sorted = sortParallelArraysByX(xValues, yValues, metadata);

        const yAxisId =
          stream === 'wgr' || stream === 'cgr'
            ? SECONDARY_Y_AXIS_ID
            : PRIMARY_Y_AXIS_ID;

        // Production line series with circle markers
        const dataSeries = new XyDataSeries(wasm, {
          xValues: sorted.xValues,
          yValues: sorted.yValues,
          dataSeriesName: `Production ${stream}`,
          metadata: sorted.metadata,
        });

        const lineSeries = new FastLineRenderableSeries(wasm, {
          id: `production-${stream}`,
          dataSeries,
          stroke: STREAM_COLORS[stream],
          strokeThickness: 3,
          yAxisId,
          isVisible: selectedSeries.includes(stream as MineralType),
          pointMarker: new EllipsePointMarker(wasm, {
            width: 6,
            height: 6,
            fill: STREAM_COLORS[stream],
            stroke: STREAM_COLORS[stream],
            strokeThickness: 1.5,
          }),
        });
        surface.renderableSeries.add(lineSeries);

        // Outlier markers overlay (hollow circles)
        if (outlierXValues.length > 0) {
          const outlierDs = new XyDataSeries(wasm, {
            xValues: outlierXValues,
            yValues: outlierYValues,
            dataSeriesName: `Outlier ${stream}`,
            metadata: outlierMeta,
          });
          const outlierSeries = new XyScatterRenderableSeries(wasm, {
            id: `outlier-production-${stream}`,
            dataSeries: outlierDs,
            yAxisId,
            isVisible: selectedSeries.includes(stream as MineralType),
            pointMarker: new EllipsePointMarker(wasm, {
              width: 8,
              height: 8,
              fill: '#FFFFFF',
              stroke: STREAM_COLORS[stream],
              strokeThickness: 3,
            }),
          });
          surface.renderableSeries.add(outlierSeries);
        }
      }

      // =================================================================
      // 3. Build forecast series
      // =================================================================

      const primaryForecast =
        enrichedForecasts.find((f) => f.isDraggable) ?? enrichedForecasts[0];

      const plotlineData = primaryForecast
        ? productionStreams
            .map((stream) => {
              const forecastData = primaryForecast[volumeMap[type]]
                .map((record) => ({
                  y: record[stream as keyof typeof record],
                  x: getXValue(record, xAxisType, earliestDate),
                }))
                .filter((r) => r.x !== null && r.y !== null)
                .sort((a, b) => a.x - b.x);
              return { name: stream, value: forecastData[0]?.x };
            })
            .filter((d) => d.value !== undefined)
        : [];

      const draggableEntries: DraggableSeriesEntry[] = [];

      for (const forecast of enrichedForecasts) {
        const forecastRecords = forecast[volumeMap[type]];

        for (const stream of productionStreams) {
          const xValues: number[] = [];
          const yValues: number[] = [];
          const metadata: VolumePointMetadata[] = [];

          for (const record of forecastRecords) {
            const yVal = record[stream as keyof typeof record] as number;
            const xVal = getXValue(record, xAxisType, earliestDate);
            if (xVal === null || yVal === null) continue;
            if (isLogYAxis && yVal <= 0) continue;

            xValues.push(xVal);
            yValues.push(yVal);
            metadata.push({
              isSelected: false,
              originalDate: moment.utc(record.date).valueOf(),
              isOutlier: false,
              cumulativeOil: record.cumulativeOil,
              cumulativeWater: record.cumulativeWater,
              cumulativeGas: record.cumulativeGas,
              materialBalanceTimeOil: record.materialBalanceTimeOil,
              materialBalanceTimeWater: record.materialBalanceTimeWater,
              materialBalanceTimeGas: record.materialBalanceTimeGas,
            });
          }

          if (xValues.length === 0) continue;

          const sortedFc = sortParallelArraysByX(xValues, yValues, metadata);

          const yAxisId =
            stream === 'wgr' || stream === 'cgr'
              ? SECONDARY_Y_AXIS_ID
              : PRIMARY_Y_AXIS_ID;

          const seriesId = `${forecast.id}-${stream}`;

          const dataSeries = new XyDataSeries(wasm, {
            xValues: sortedFc.xValues,
            yValues: sortedFc.yValues,
            dataSeriesName: `${forecast.name} ${stream}`,
            metadata: sortedFc.metadata,
          });

          const isSolid = forecast.lineStyle === 'solid';

          const lineSeries = new FastLineRenderableSeries(wasm, {
            id: seriesId,
            dataSeries,
            stroke: STREAM_COLORS[stream],
            // A dashed forecast is a pixel heavier than the production line so
            // its dashes read clearly where the two overlap. A solid line is
            // another production version, so it matches the production line.
            strokeThickness: isSolid ? 3 : 4,
            ...(isSolid ? {} : { strokeDashArray: [5, 5] }),
            yAxisId,
            isVisible: selectedSeries.includes(stream as MineralType),
          });
          surface.renderableSeries.add(lineSeries);

          // Register draggable entries
          if (forecast.isDraggable && draggableSeriesConfig) {
            const config =
              draggableSeriesConfig[
                stream as keyof typeof draggableSeriesConfig
              ];
            if (config && (config.isXDraggable || config.isYDraggable)) {
              draggableEntries.push({
                seriesId,
                stream,
                isXDraggable: config.isXDraggable,
                isYDraggable: config.isYDraggable,
              });
            }
          }
        }
      }

      // =================================================================
      // 4. Build type curve series
      // =================================================================

      if (showTypeCurve && enrichedTypeCurveData.length > 0) {
        const typeCurveStreams =
          type === 'ratio'
            ? ['gor', 'wor', 'wgr', 'cgr']
            : ['oil', 'gas', 'water'];

        for (const baseStream of typeCurveStreams) {
          const xValues: number[] = [];
          const yValues: number[] = [];
          const metadata: VolumePointMetadata[] = [];

          for (const record of enrichedTypeCurveData) {
            let yVal: number;
            if (type === 'ratio') {
              yVal = record[baseStream as keyof typeof record] as number;
            } else if (type === 'cumulative') {
              const key = `cumulative${baseStream
                .charAt(0)
                .toUpperCase()}${baseStream.slice(1)}` as keyof typeof record;
              yVal = record[key] as number;
            } else {
              yVal = record[baseStream as keyof typeof record] as number;
            }

            if (!yVal || yVal <= 0) continue;

            const xVal = getXValue(
              {
                date: moment.utc(record.date).format('YYYY-MM-DD'),
                oil: record.oil,
                gas: record.gas,
                water: record.water,
                cumulativeOil: record.cumulativeOil,
                cumulativeWater: record.cumulativeWater,
                cumulativeGas: record.cumulativeGas,
                materialBalanceTimeOil: record.materialBalanceTimeOil,
                materialBalanceTimeWater: record.materialBalanceTimeWater,
                materialBalanceTimeGas: record.materialBalanceTimeGas,
              } as EnrichedVolumeRecord,
              xAxisType,
              earliestProductionDate
            );

            if (xVal === null) continue;

            xValues.push(xVal);
            yValues.push(yVal);
            metadata.push({
              isSelected: false,
              originalDate: moment.utc(record.date).valueOf(),
              isOutlier: false,
              cumulativeOil: record.cumulativeOil,
              cumulativeWater: record.cumulativeWater,
              cumulativeGas: record.cumulativeGas,
              materialBalanceTimeOil: record.materialBalanceTimeOil,
              materialBalanceTimeWater: record.materialBalanceTimeWater,
              materialBalanceTimeGas: record.materialBalanceTimeGas,
            });
          }

          if (xValues.length === 0) continue;

          const sortedTc = sortParallelArraysByX(xValues, yValues, metadata);

          const yAxisId =
            baseStream === 'wgr' || baseStream === 'cgr'
              ? SECONDARY_Y_AXIS_ID
              : PRIMARY_Y_AXIS_ID;

          const dataSeries = new XyDataSeries(wasm, {
            xValues: sortedTc.xValues,
            yValues: sortedTc.yValues,
            dataSeriesName: `t${baseStream}`,
            metadata: sortedTc.metadata,
          });

          const lineSeries = new FastLineRenderableSeries(wasm, {
            id: `type-curve-${baseStream}`,
            dataSeries,
            stroke: STREAM_COLORS[baseStream],
            strokeThickness: 1.5,
            opacity: 0.5,
            yAxisId,
            isVisible:
              showTypeCurve &&
              selectedSeries.some(
                (s) => s.toLowerCase() === baseStream.toLowerCase()
              ),
          });
          surface.renderableSeries.add(lineSeries);
        }
      }

      // =================================================================
      // 5. Build segment marker annotations (triangles at chart bottom)
      // =================================================================

      if (segmentDates && showMarkers?.segmentation) {
        const shouldShowMarker = (stream: 'oil' | 'gas' | 'water'): boolean => {
          if (type === 'ratio') {
            const hasGOR = selectedSeries.includes('gor');
            const hasWOR = selectedSeries.includes('wor');
            const hasWGR = selectedSeries.includes('wgr');
            if (stream === 'oil') return hasGOR || hasWOR;
            if (stream === 'water') return hasWOR || hasWGR;
            if (stream === 'gas') return hasGOR || hasWGR;
            return true;
          }
          return selectedSeries.includes(stream);
        };

        // Collect markers grouped by x-value for stacking
        const markersByX = new Map<
          number,
          Array<{ stream: string; color: string }>
        >();

        for (const stream of ['oil', 'gas', 'water'] as const) {
          const dates = segmentDates[stream];
          if (!dates || dates.length === 0 || !shouldShowMarker(stream))
            continue;

          for (const dateStr of dates) {
            const dateObj = moment.utc(dateStr).toDate();
            const forecastForConversion = forecasts[0] || {
              id: '',
              name: '',
              volume: [],
              cumulative: [],
              ratio: [],
              materialBalanceTime: [],
            };
            const xVal = convertDateToXAxisValue({
              date: dateObj,
              targetXAxisType: xAxisType,
              production,
              forecast: forecastForConversion,
              chartType: type,
              earliestDate,
            });
            if (xVal === null) continue;

            if (!markersByX.has(xVal)) markersByX.set(xVal, []);
            markersByX.get(xVal)!.push({
              stream,
              color: STREAM_COLORS[stream],
            });
          }
        }

        const streamOrder: Record<string, number> = {
          oil: 0,
          water: 1,
          gas: 2,
        };

        for (const [xVal, markers] of markersByX) {
          const sorted = [...markers].sort(
            (a, b) =>
              (streamOrder[a.stream] ?? 0) - (streamOrder[b.stream] ?? 0)
          );
          for (let i = 0; i < sorted.length; i++) {
            const height = sorted.length === 1 ? 15 : 12 + i * 6;
            const { color } = sorted[i];

            // SVG upward-pointing triangle: tip at top, base at bottom
            const svgWidth = 6;
            const svgStr = `<svg width="${svgWidth}" height="${height}" xmlns="http://www.w3.org/2000/svg">
            <polygon points="${
              svgWidth / 2
            },0 0,${height} ${svgWidth},${height}" fill="${color}"/>
          </svg>`;

            surface.annotations.add(
              new CustomAnnotation({
                x1: xVal,
                y1: 1,
                xCoordinateMode: ECoordinateMode.DataValue,
                yCoordinateMode: ECoordinateMode.Relative,
                verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
                horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
                svgString: svgStr,
                annotationLayer: EAnnotationLayer.AboveChart,
                xAxisId: DEFAULT_X_AXIS_ID,
                yAxisId: PRIMARY_Y_AXIS_ID,
              })
            );
          }
        }
      }

      // =================================================================
      // 6. Build draggable plot lines (forecast start date markers)
      // =================================================================

      for (const pl of plotlineData) {
        if (pl.value === undefined) continue;

        const annotation = new VerticalLineAnnotation({
          id: `plotline-${pl.name}`,
          x1: pl.value,
          stroke: STREAM_COLORS[pl.name],
          strokeThickness: 3,
          strokeDashArray: [4, 4],
          isEditable: true,
          annotationLayer: EAnnotationLayer.BelowChart,
          xAxisId: DEFAULT_X_AXIS_ID,
          yAxisId: PRIMARY_Y_AXIS_ID,
          showLabel: false,
          isHidden: !selectedSeries.includes(pl.name as MineralType),
        });

        // Snap to nearest data point on drag end
        annotation.dragEnded.subscribe(() => {
          const newValue = annotation.x1 as number;
          let snappedDate: Date;

          if (xAxisType === 'date') {
            const m = parseMomentUtc(newValue);
            const startOfMonth = m.clone().startOf('month');
            const startOfNext = m.clone().add(1, 'month').startOf('month');
            const snappedMs =
              Math.abs(newValue - startOfMonth.valueOf()) <
              Math.abs(newValue - startOfNext.valueOf())
                ? startOfMonth.valueOf()
                : startOfNext.valueOf();
            annotation.x1 = snappedMs;
            snappedDate = parseMomentUtc(snappedMs).toDate();
          } else if (xAxisType === 'count') {
            const rounded = Math.round(newValue);
            annotation.x1 = rounded;
            snappedDate = moment
              .utc(earliestDate)
              .add(rounded - 1, 'months')
              .toDate();
          } else {
            // Cumulative/MBT: snap to nearest production data point. A daily
            // row's date is moved to its month's start, as the regression
            // works in months.
            const snapped = snapToNearestRecord<
              EnrichedVolumeRecord | EnrichedRatioRecord
            >(
              enrichedProduction[volumeMap[type]],
              newValue,
              (record) => getXValue(record, xAxisType, earliestDate),
              productionVolumeIsDailyRate
            );
            annotation.x1 = snapped?.x ?? annotation.x1;
            snappedDate = snapped?.date ?? moment.utc().toDate();
          }

          onDragEnd(
            snappedDate,
            pl.name as MineralType | 'gor' | 'wor' | 'wgr' | 'cgr'
          );
        });

        surface.annotations.add(annotation);
      }

      // =================================================================
      // 7. Chart modifiers (zoom, tooltip, point drag)
      // =================================================================

      // Data point drag modifier must be registered first so it captures
      // clicks on data points before ZoomPanModifier starts a pan.
      if (draggableEntries.length > 0) {
        const dragModifier = new DataPointDragModifier();
        dragModifier.setConfig(
          draggableEntries,
          reportDragUpdate,
          earliestDate,
          xAxisType
        );
        surface.chartModifiers.add(dragModifier);
      }

      // Scroll to zoom
      surface.chartModifiers.add(new MouseWheelZoomModifier());

      // Drag to scroll/pan the chart
      surface.chartModifiers.add(new ZoomPanModifier());

      // Per-axis drag
      surface.chartModifiers.add(new XAxisDragModifier());
      surface.chartModifiers.add(new YAxisDragModifier());

      // Double-click to restore the view the chart opened on.
      class ResetZoomModifier extends ChartModifierBase2D {
        readonly type = 'ResetZoomModifier';
        override modifierDoubleClick(args: ModifierMouseArgs) {
          if (!this.parentSurface) return;

          resetToBaseline(this.parentSurface);
          args.handled = true;
        }
      }
      surface.chartModifiers.add(new ResetZoomModifier());

      // Crosshair: vertical + horizontal intersecting lines on hover
      surface.chartModifiers.add(
        new CursorModifier({
          showTooltip: false,
          showAxisLabels: false,
          crosshairStroke: '#a8a8a8',
          crosshairStrokeDashArray: [4, 4],
        })
      );

      // Custom tooltip modifier with hit-testing
      const tooltipModifier = new TooltipChartModifier();
      tooltipModifier.setConfig({
        tooltipElement: tooltipRef.current,
        containerElement: chartContainerRef.current,
        chartType: type,
        xAxisType,
        decimalsForChart,
        dateFormat: pointDateFormat(Boolean(productionVolumeIsDailyRate)),
        firstProductionDate,
      });
      surface.chartModifiers.add(tooltipModifier);

      // Fit all data into view, then put back the ranges this rebuild pinned:
      // zoomExtents ranges every axis on the raw data and would otherwise undo
      // the log chart's decade snapping and the ratio chart's percentiles.
      surface.zoomExtents();
      applyYRangeOverrides(surface);

      if (initialAutoZoom) {
        // Counted in calendar months, so daily production (many rows a
        // month) zooms the same as monthly production (one row a month).
        const maxMonthsOnline = new Set(
          productionData.filter((r) => r.date).map((r) => monthOf(r.date))
        ).size;
        const zoomMonths = Math.max(2 * maxMonthsOnline, 50);
        const xAxisRef = surface.xAxes.getById(DEFAULT_X_AXIS_ID);
        if (xAxisRef) {
          const fullRange = xAxisRef.visibleRange;

          const records: (EnrichedVolumeRecord | EnrichedRatioRecord)[] = [
            ...productionData,
            ...enrichedForecasts.flatMap(
              (forecast): (EnrichedVolumeRecord | EnrichedRatioRecord)[] =>
                forecast[volumeMap[type]]
            ),
          ].filter((r) => r.date);
          // `YYYY-MM` sorts in date order.
          const months = [
            ...new Set(records.map((r) => monthOf(r.date))),
          ].sort();

          let zoomMax: number | undefined;

          if (months.length >= zoomMonths) {
            // More data than the zoom window: zoom in to the window's largest
            // x. Not the x of its last month, since Material Balance Time
            // doesn't grow with the date and that would cut points off.
            const windowMonths = new Set(months.slice(0, zoomMonths));
            zoomMax = -Infinity;
            for (const r of records) {
              if (!windowMonths.has(monthOf(r.date))) continue;
              const x = getXValue(r, xAxisType, earliestDate);
              if (isFinite(x) && x > zoomMax) zoomMax = x;
            }
            if (zoomMax === -Infinity) zoomMax = undefined;
          } else if (months.length > 0) {
            // Less data than the zoom window — extend axis OUT to zoomMonths
            if (xAxisType === 'count') {
              zoomMax = zoomMonths;
            } else if (xAxisType === 'date') {
              zoomMax = earliestDate
                .clone()
                .add(zoomMonths - 1, 'months')
                .valueOf();
            }
          }

          if (
            zoomMax !== undefined &&
            Math.abs(zoomMax - fullRange.max) > 0.5
          ) {
            xAxisRef.visibleRange = new NumberRange(fullRange.min, zoomMax);
          }
        }
      }

      // Whatever the chart ended up showing is the view it opened on, so the
      // Reset zoom button has nothing to go back to until the user moves off
      // it. The auto-zoom window counts as the opening view, not as a zoom.
      const openingXAxis = surface.xAxes.getById(DEFAULT_X_AXIS_ID);
      baselineXRangeRef.current = openingXAxis?.visibleRange ?? null;

      restoreRanges(surface, keptRanges);

      // A rebuild that put the user's zoom back is still zoomed. This runs
      // before the subscription below exists, so nothing else would say so,
      // and the next rebuild reads this to decide whether the view is worth
      // carrying over.
      setIsZoomed(!!keptRanges);

      surface.resumeUpdates();

      // The Reset zoom button appears once the view moves off the one the
      // chart opened on, and goes away again when it comes back.
      if (openingXAxis) {
        openingXAxis.visibleRangeChanged.subscribe((args) => {
          const baseline = baselineXRangeRef.current;
          if (!args || !baseline) return;

          const { min, max } = args.visibleRange;
          const isAtBaseline =
            Math.abs(min - baseline.min) < 1 &&
            Math.abs(max - baseline.max) < 1;
          setIsZoomed(!isAtBaseline);
        });
      }
    }); // end scheduleChartRebuild

    return cancelRebuild;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    xAxisType,
    enrichedProduction,
    enrichedForecasts,
    type,
    earliestDate,
    reportDragUpdate,
    draggableSeriesConfig,
    decimalsForChart,
    outlierDates,
    showMarkers,
    primaryStream,
    typeCurveVolumes,
    showTypeCurve,
    enrichedTypeCurveData,
    earliestProductionDate,
    firstProductionDate,
    productionVolumeIsDailyRate,
    dailyScale,
  ]);

  // -----------------------------------------------------------------------
  // Lightweight series visibility toggle (no rebuild)
  // -----------------------------------------------------------------------

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    surface.renderableSeries.asArray().forEach((series) => {
      const id = series.id ?? '';

      // Outlier overlays follow their parent production series visibility
      if (id.startsWith('outlier-production-')) {
        const stream = id.replace('outlier-production-', '');
        series.isVisible = selectedSeries.includes(stream as MineralType);
        return;
      }

      // Type curve series
      if (id.startsWith('type-curve-')) {
        const baseStream = id.replace('type-curve-', '');
        series.isVisible =
          showTypeCurve &&
          selectedSeries.some(
            (s) => s.toLowerCase() === baseStream.toLowerCase()
          );
        return;
      }

      // Production and forecast series: ID ends with "-{stream}"
      const stream = id.split('-').pop() ?? '';
      series.isVisible = selectedSeries.includes(stream as MineralType);
    });

    surface.annotations.asArray().forEach((annotation) => {
      const id = annotation.id ?? '';
      if (!id.startsWith('plotline-')) return;
      const stream = id.replace('plotline-', '');
      annotation.isHidden = !selectedSeries.includes(stream as MineralType);
    });
  }, [selectedSeries, showTypeCurve]);

  // -----------------------------------------------------------------------
  // Responsive resizing
  // -----------------------------------------------------------------------

  // The canvas width is pinned in pixels here, so until this runs the chart
  // keeps the width it had. `leading` applies the first size change at once,
  // which is what a one-off change like expanding a chart produces; the
  // trailing call still covers the stream of changes a window drag produces.
  // While an image export has the canvas at the image's size, it is left alone,
  // and the size is applied once the export ends.
  const resizeDuringExportRef = useRef<ResizeObserverEntry[] | null>(null);
  const handleResize = useMemo(
    () =>
      debounce(
        (entries: ResizeObserverEntry[]) => {
          const divEl = canvasDivRef.current;
          if (divEl?.hasAttribute(CHART_EXPORTING_ATTRIBUTE)) {
            resizeDuringExportRef.current = entries;
            return;
          }

          const { width } = entries[entries.length - 1].contentRect;
          setMeasuredWidth(width);

          if (divEl) {
            const canvasHeight =
              fixedHeight ?? Math.round(width / CHART_ASPECT_RATIO);
            divEl.style.width = `${width}px`;
            divEl.style.height = `${canvasHeight}px`;
          }
        },
        600,
        { leading: true }
      ),
    [fixedHeight]
  );

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(chartContainerRef.current);

    const divEl = canvasDivRef.current;
    const exportObserver = new MutationObserver(() => {
      const entries = resizeDuringExportRef.current;
      if (!entries || divEl?.hasAttribute(CHART_EXPORTING_ATTRIBUTE)) return;
      resizeDuringExportRef.current = null;
      handleResize(entries);
    });
    if (divEl) {
      exportObserver.observe(divEl, {
        attributeFilter: [CHART_EXPORTING_ATTRIBUTE],
      });
    }

    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
      exportObserver.disconnect();
    };
  }, [handleResize]);

  // -----------------------------------------------------------------------
  // Reset zoom handler
  // -----------------------------------------------------------------------

  const handleResetZoom = useCallback(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    resetToBaseline(surface);
  }, [resetToBaseline]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div
      ref={chartContainerRef}
      className={style.chartContainer}
      style={{ position: 'relative' }}
    >
      {/* A titled chart puts the title and whatever the host passes in one
          row, and that row reserves space on its right for the export
          trigger, which anchors itself to the top-right of this container.
          An untitled chart (the workspace grid) supplies its own full row. */}
      {title ? (
        <div className={style.chartTitle}>
          <span className={style.chartTitleText}>{title}</span>
          {header}
        </div>
      ) : (
        header
      )}

      {headerStats && headerStats.length > 0 && (
        <div className={style.headerStats}>
          {headerStats.map(({ label, value, unit }) => (
            <span key={label} className={style.headerStat}>
              {`${label} `}
              <strong>{formatHeaderStat(value)}</strong>
              {` ${unit}`}
            </span>
          ))}
        </div>
      )}

      {exportConfig && (
        <SciChartExportImageButton
          iconOnly
          surfaceRef={surfaceRef}
          containerRef={canvasDivRef}
          chartLabel={exportConfig.label}
          filenamePrefix={exportConfig.filenamePrefix}
          className={exportConfig.iconClassName}
          elementHooks={exportElementHooks}
          subtitle={exportSubtitle}
        />
      )}

      {/* SciChart canvas container — explicit height needed for WebGL canvas;
          position: relative so it's the export capture container.
          The export marks this div with CHART_EXPORTING_ATTRIBUTE, and the
          resize handling above reads it through the same canvasDivRef.
          data-chart-canvas lets a parent find it to measure the room left. */}
      <div
        ref={canvasDivRef}
        id={chartDivId}
        data-chart-canvas
        style={{ position: 'relative', width: '100%', height: `${height}px` }}
      />

      {/* Reset zoom button — appears only when user has zoomed in */}
      {isZoomed && (
        <button
          onClick={handleResetZoom}
          style={{
            position: 'absolute',
            top: '50px',
            right: '10px',
            zIndex: 60,
            padding: '4px 8px',
            fontSize: '11px',
            backgroundColor: '#ffffff',
            border: '1px solid #999',
            borderRadius: '3px',
            cursor: 'pointer',
            color: '#333',
          }}
        >
          Reset zoom
        </button>
      )}

      {/* Custom tooltip overlay */}
      <div
        ref={tooltipRef}
        style={{
          display: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 100,
          pointerEvents: 'none',
        }}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            fontSize: '14px',
            color: '#666',
          }}
        >
          Loading...
        </div>
      )}

      {/* Footer (e.g. XAxisSwitcher) */}
      {footer}
    </div>
  );
};
